import React, { useState, useEffect } from "react";
import {
  Stack,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  Box,
} from "@mui/material";
import { MdLockOutline, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { themeOrange } from "../../../utils/colorTheme";

/**
 * Client-side password gate for a sub-section of the admin panel.
 * Renders an inline centered card (NOT a modal) so the sidebar stays usable.
 *
 * Note: client-side gates are a soft-lock for casual browsing protection only.
 * Anyone who views the JS bundle can see the password.
 */
const PasswordGate = ({
  gateKey = "admin_section_gate",
  expected,
  title = "Enter password to continue",
  hint = "",
  children,
}) => {
  const [unlocked, setUnlocked] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(gateKey) === "1"
  );
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem(gateKey) === "1") {
      setUnlocked(true);
    }
  }, [gateKey]);

  const attempt = () => {
    if (value === expected) {
      localStorage.setItem(gateKey, "1");
      setUnlocked(true);
      setError("");
      setValue("");
    } else {
      setError("Incorrect password");
    }
  };

  if (unlocked) return children;

  // Inline, centered card — no modal overlay blocking the sidebar
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        minHeight: "60vh",
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          borderRadius: "16px",
          boxShadow: "0px 4px 10px 0px #00000026",
          mt: { xs: 2, md: 6 },
        }}
      >
        <Stack direction="row" gap={1} alignItems="center" mb={1}>
          <Box sx={{ color: themeOrange, display: "flex" }}>
            <MdLockOutline size={22} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "18px" }}>
            {title}
          </Typography>
        </Stack>
        <Typography sx={{ color: "#666", fontSize: "13px", mb: 2 }}>
          {hint || "You can still navigate to any other section from the sidebar."}
        </Typography>
        <Stack gap={2}>
          <TextField
            autoFocus
            label="Password"
            type={show ? "text" : "password"}
            size="small"
            fullWidth
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") attempt(); }}
            error={!!error}
            helperText={error || " "}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShow((s) => !s)}>
                    {show ? <MdVisibilityOff /> : <MdVisibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={attempt}
            disabled={!value}
            sx={{
              backgroundColor: themeOrange,
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              "&:hover": { backgroundColor: "#d94f0f" },
              "&:disabled": { backgroundColor: "#fca5a5", color: "white" },
            }}
          >
            Unlock
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PasswordGate;
